import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useEffect, useState, useMemo } from 'react'
import { useEvent } from '~/client/event/use-event.hook'
import { useEventAccess } from '~/client/event/use-event-access.hook'
import { EventSpaceList, EventSpaceListMembership, ConsolidatedSpace } from '~/shared/event/event.types'
import { formatUtcDateLongFromUnknown } from '~/shared/common/date'
import { normalize, shortlistAndNormalize } from '~/client/components/present/WP2Template/transform'
import { getTemplateDefinition } from '~/client/components/present/common/templateRegistry'
import {
  instantiateSlide,
  buildSpaceVariables,
} from '~/client/components/present/common/presentationGenerator'
import { exportSlideToHtml, RICHTEXT_CSS } from '~/client/components/slide-editor/exportHtml'
import HtmlSlide from '~/client/components/present/common/HtmlSlide'
import type { SlideTemplateDefinition } from '~/client/components/present/common/templateDefinition'
import {
  Carousel,
  CarouselClose,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '~/client/components/ui/carousel'
import { usePhotoGallery } from '~/client/file/use-photo-gallery.hook'
import { getPresentationPhotos } from '~/client/components/present/common/presentation-photos'
import { buildImageUrl } from '~/shared/common/image-url'
import { NotFound } from '~/client/components/NotFound'

export const Route = createFileRoute(
  '/_app/events/$eventSlug_/present/template/$templateName',
)({
  component: RouteComponent,
  validateSearch: (search: { mode?: string; view?: string } = {}) => ({
    mode: search.mode === 'all' ? 'all' : ('shortlisted' as const),
    view: search.view === 'cover' ? 'cover' : undefined,
  }),
})

const BASE_WIDTH = 1920
const BASE_HEIGHT = 1080

// ---------------------------------------------------------------------------
// Space slide — fetches photos then renders via templateDef
// ---------------------------------------------------------------------------

/** Max photos on the regular space slide. */
const PHOTOS_PER_SPACE_SLIDE = 3
/** Max photos per additional-photos overflow slide (2×2 grid). */
const PHOTOS_PER_ADDITIONAL_SLIDE = 4

/**
 * Resolve a photo record into a displayable URL string.
 */
function resolvePhotoUrl(photo: { url?: string | null; upload?: string | null }): string {
  if (photo.url) return photo.url
  if (photo.upload) {
    return buildImageUrl(
      photo.upload.startsWith('media/') ? photo.upload : `media/${photo.upload}`,
      { w: 1200, h: 1000 },
    )
  }
  return ''
}

/**
 * Hook that returns the carousel items (HTML strings) for a single space:
 * one space slide + zero-or-more additional-photos overflow slides.
 */
function useSpaceSlides(
  spaceTemplate: SlideTemplateDefinition,
  additionalPhotosTemplate: SlideTemplateDefinition | undefined,
  membership: EventSpaceListMembership,
  consolidatedSpace: ConsolidatedSpace,
  index: number,
  eventName: string,
  eventDate: string,
): string[] {
  const { data: galleryData } = usePhotoGallery(membership.id)

  // Resolve ALL photo URLs (not just the first 3)
  const imageUrls = React.useMemo(() => {
    if (!galleryData) return [] as string[]
    const photos = getPresentationPhotos(
      galleryData.all_photos,
      membership.id,
      true,
    )
    return photos.map(resolvePhotoUrl).filter(Boolean)
  }, [galleryData, membership.id])

  const spaceVars = useMemo(
    () => buildSpaceVariables(membership, consolidatedSpace || {}, index, imageUrls),
    [membership, consolidatedSpace, index, imageUrls],
  )

  return useMemo(() => {
    const allVars = { ...spaceVars, eventName, eventDate }
    const slideName = spaceVars.venueName || `Space ${index + 1}`

    // 1. Regular space slide (uses image1Url–image3Url)
    const spaceSlide = instantiateSlide(spaceTemplate, allVars, slideName)
    const htmls = [exportSlideToHtml(spaceSlide)]

    // 2. Additional-photos overflow slides (if template supports it)
    if (additionalPhotosTemplate && imageUrls.length > PHOTOS_PER_SPACE_SLIDE) {
      const extraUrls = imageUrls.slice(PHOTOS_PER_SPACE_SLIDE)

      for (let page = 0; page < extraUrls.length; page += PHOTOS_PER_ADDITIONAL_SLIDE) {
        const batch = extraUrls.slice(page, page + PHOTOS_PER_ADDITIONAL_SLIDE)
        const photoVars: Record<string, string> = {
          eventName,
          eventDate,
          optionNumber: spaceVars.optionNumber,
          venueName: spaceVars.venueName,
          spaceName: spaceVars.spaceName,
          additionalPhoto1: batch[0] || '',
          additionalPhoto2: batch[1] || '',
          additionalPhoto3: batch[2] || '',
          additionalPhoto4: batch[3] || '',
        }
        const pageNum = Math.floor(page / PHOTOS_PER_ADDITIONAL_SLIDE) + 1
        const photoSlideName = `${slideName} — Additional Photos${extraUrls.length > PHOTOS_PER_ADDITIONAL_SLIDE ? ` (${pageNum})` : ''}`
        const photoSlide = instantiateSlide(additionalPhotosTemplate, photoVars, photoSlideName)
        htmls.push(exportSlideToHtml(photoSlide))
      }
    }

    return htmls
  }, [spaceTemplate, additionalPhotosTemplate, spaceVars, eventName, eventDate, index, imageUrls])
}

const SpaceSlideWithPhotos: React.FC<{
  spaceTemplate: SlideTemplateDefinition
  additionalPhotosTemplate?: SlideTemplateDefinition
  membership: EventSpaceListMembership
  consolidatedSpace: ConsolidatedSpace
  index: number
  eventName: string
  eventDate: string
}> = ({ spaceTemplate, additionalPhotosTemplate, membership, consolidatedSpace, index, eventName, eventDate }) => {
  const slideHtmls = useSpaceSlides(
    spaceTemplate,
    additionalPhotosTemplate,
    membership,
    consolidatedSpace,
    index,
    eventName,
    eventDate,
  )

  return (
    <>
      {slideHtmls.map((html, i) => (
        <CarouselItem key={`space-${index}-slide-${i}`} style={{ height: BASE_HEIGHT }}>
          <div className="slide-present-richtext" style={{ width: '100%', height: '100%' }}>
            <HtmlSlide html={html} />
          </div>
        </CarouselItem>
      ))}
    </>
  )
}

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

function RouteComponent() {
  const navigate = useNavigate()
  const { eventSlug, templateName } = Route.useParams()
  const { mode, view } = Route.useSearch()
  const eventAccessQuery = useEventAccess(eventSlug)
  const { data: event, isLoading } = useEvent(eventSlug)
  const [fadePhase, setFadePhase] = useState<
    'initial' | 'black' | 'presentation'
  >('initial')

  const eventSpaceList: EventSpaceList | null = event
    ? (event as EventSpaceList)
    : null
  const rawMemberships: EventSpaceListMembership[] = (event?.space_memberships ||
    []) as EventSpaceListMembership[]
  const eventSpaceMemberships: EventSpaceListMembership[] =
    mode === 'all'
      ? normalize(rawMemberships)
      : shortlistAndNormalize(rawMemberships)

  const templateDef = getTemplateDefinition(templateName)

  // Fade transition
  useEffect(() => {
    const t1 = setTimeout(() => setFadePhase('black'), 10)
    const t2 = setTimeout(() => setFadePhase('presentation'), 600)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [])

  // ── Inject Google Fonts into <head> for reliable font loading ──────────
  useEffect(() => {
    if (!templateDef?.googleFontsUrl) return

    const fontsUrl = templateDef.googleFontsUrl
    const headLinks: HTMLElement[] = []

    // preconnect to Google Fonts origins
    const preconnect1 = document.createElement('link')
    preconnect1.rel = 'preconnect'
    preconnect1.href = 'https://fonts.googleapis.com'
    document.head.appendChild(preconnect1)
    headLinks.push(preconnect1)

    const preconnect2 = document.createElement('link')
    preconnect2.rel = 'preconnect'
    preconnect2.href = 'https://fonts.gstatic.com'
    preconnect2.crossOrigin = ''
    document.head.appendChild(preconnect2)
    headLinks.push(preconnect2)

    // stylesheet for the template's googleFontsUrl
    const fontLink = document.createElement('link')
    fontLink.rel = 'stylesheet'
    fontLink.href = fontsUrl
    document.head.appendChild(fontLink)
    headLinks.push(fontLink)

    return () => {
      headLinks.forEach((el) => el.remove())
    }
  }, [templateDef?.googleFontsUrl])

  // Responsive scaling + Escape to close
  const [scale, setScale] = useState(1)

  const carouselRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node) node.focus()
  }, [])

  useEffect(() => {
    const computeScale = () =>
      setScale(
        Math.min(
          window.innerWidth / BASE_WIDTH,
          window.innerHeight / BASE_HEIGHT,
        ),
      )
    computeScale()
    window.addEventListener('resize', computeScale)

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        navigate({ to: '/events/$eventSlug', params: { eventSlug }, search: { tab: 'shortlist' } } as any)
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('resize', computeScale)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [navigate, eventSlug])

  if (
    eventAccessQuery.data?.access === 'forbidden' ||
    eventAccessQuery.data?.access === 'not_found' ||
    (!isLoading && !eventAccessQuery.isLoading && !eventSpaceList) ||
    !templateDef
  ) {
    return <NotFound />
  }

  // ------ Loading ------
  if (isLoading || eventAccessQuery.isLoading || !eventSpaceList) {
    return (
      <div className="absolute inset-0 w-full h-full z-50 bg-black flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    )
  }

  // ------ Build cover slide ------
  const eventDate =
    formatUtcDateLongFromUnknown(
      eventSpaceList.event_space_list_dates?.[0]?.start_date,
      'en-US',
    ) ?? ''

  const coverSlide = instantiateSlide(templateDef.cover, {
    eventName: eventSpaceList.name || '',
    eventDate,
    venueGroupName: eventSpaceList.venue_group_name || '',
  }, 'Cover')
  const renderedCoverHtml = exportSlideToHtml(coverSlide)

  const closePresentation = () =>
    navigate({ to: '/events/$eventSlug', params: { eventSlug }, search: { tab: 'shortlist' } } as any)

  // ------ Cover-only view (iframe embeds / previews) ------
  if (view === 'cover') {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-black">
        <style dangerouslySetInnerHTML={{ __html: `.slide-present-richtext ${RICHTEXT_CSS}` }} />
        <div
          style={{
            width: BASE_WIDTH,
            height: BASE_HEIGHT,
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
          }}
        >
          <div className="slide-present-richtext" style={{ width: '100%', height: '100%' }}>
            <HtmlSlide html={renderedCoverHtml} />
          </div>
        </div>
      </div>
    )
  }

  // ------ Full presentation (carousel) ------
  return (
    <div className="absolute inset-0 w-full h-full z-50">
      <style dangerouslySetInnerHTML={{ __html: `.slide-present-richtext ${RICHTEXT_CSS}` }} />

      {/* Black overlay that fades in */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${fadePhase === 'initial' ? 'opacity-0' : 'opacity-100'
          }`}
      />

      {/* Presentation content that fades in after black screen */}
      <div
        className={`flex justify-center items-center absolute inset-0 transition-opacity duration-500 ${fadePhase === 'presentation' ? 'opacity-100' : 'opacity-0'
          }`}
      >
        <div className="w-full h-screen flex items-center justify-center bg-black">
          <div
            style={{
              width: BASE_WIDTH,
              height: BASE_HEIGHT,
              transform: `scale(${scale})`,
              transformOrigin: 'center center',
            }}
          >
            <Carousel
              ref={carouselRef}
              className="relative overflow-hidden"
              opts={{ loop: false, align: 'start' }}
              tabIndex={0}
            >
              <CarouselContent style={{ height: BASE_HEIGHT }}>
                {/* Cover slide */}
                <CarouselItem style={{ height: BASE_HEIGHT }}>
                  <div className="slide-present-richtext" style={{ width: '100%', height: '100%' }}>
                    <HtmlSlide html={renderedCoverHtml} />
                  </div>
                </CarouselItem>

                {/* Space slides (+ additional photos overflow slides) */}
                {eventSpaceMemberships.map((membership, index) => (
                  <SpaceSlideWithPhotos
                    key={membership.id}
                    spaceTemplate={templateDef.space}
                    additionalPhotosTemplate={templateDef.additionalPhotos}
                    membership={membership}
                    consolidatedSpace={
                      membership.consolidatedSpace as ConsolidatedSpace
                    }
                    index={index}
                    eventName={eventSpaceList.name || ''}
                    eventDate={eventDate}
                  />
                ))}
              </CarouselContent>

              <CarouselPrevious className="left-4 top-1/2 -translate-y-1/2 z-50 bg-white/80 shadow hover:bg-white" />
              <CarouselNext className="right-4 top-1/2 -translate-y-1/2 z-50 bg-white/80 shadow hover:bg-white" />
              <CarouselClose
                onClick={closePresentation}
                className="shadow hover:bg-white"
              />
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  )
}
